import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import { Button, TextField, Typography, Box, Alert, IconButton } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import API_URL from '../config';

function ResetPassword() {
  const { token } = useParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isMatching, setIsMatching] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState(null);

  const navigate = useNavigate();

  const validatePassword = (password) => {
    const minLength = 8;
    const hasNumber = /\d/;
    const hasUppercase = /[A-Z]/;
    const hasLowercase = /[a-z]/;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;
  
    if (password.length < minLength) {
      return 'Das Passwort muss mindestens 8 Zeichen lang sein.';
    }
    if (!hasNumber.test(password)) {
      return 'Das Passwort muss mindestens eine Zahl enthalten.';
    }
    if (!hasUppercase.test(password)) {
      return 'Das Passwort muss mindestens einen Großbuchstaben enthalten.';
    }
    if (!hasLowercase.test(password)) {
      return 'Das Passwort muss mindestens einen Kleinbuchstaben enthalten.';
    }
    if (!hasSpecialChar.test(password)) {
      return 'Das Passwort muss mindestens ein Sonderzeichen enthalten.';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
  
    if (name === 'newPassword') {
      setNewPassword(value);
      setPasswordError(validatePassword(value));
  
      // Überprüfung für isMatching muss mit dem zukünftigen confirmPassword durchgeführt werden
      setIsMatching(value === confirmPassword);
    }
  
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
      
      // Überprüfung für isMatching muss mit dem zukünftigen newPassword durchgeführt werden
      setIsMatching(newPassword === value);
    }
  };  

  const handleReset = async () => {
    setPasswordError('');
  
    // Überprüfe die Passwortrichtlinien
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
  
    if (newPassword !== confirmPassword) {
      setPasswordError('Die neuen Passwörter stimmen nicht überein.');
      return;
    }
  
    try {
      const response = await axiosInstance.post(`/auth/reset-password/${token}`, { newPassword });
      setMessage(response.data.message);
      setTimeout(() => navigate('/pages/login'), 3000);
    } catch (error) {
      setMessage("Fehler beim Zurücksetzen des Passworts.");
    }
  };  

  return (
    <Box sx={{ width: '100%', maxWidth: '500px', margin: '10vh auto'}}>
      <Typography variant="h4">Neues Passwort eingeben</Typography>
      {message && <Alert severity="success">{message}</Alert>}
      <TextField
        required
        label="Neues Passwort"
        name="newPassword"
        type={showNewPassword ? 'text' : 'password'}
        value={newPassword}
        onChange={handlePasswordChange}
        fullWidth
        margin="normal"
        error={!!passwordError}
        helperText={passwordError}
        InputProps={{
          endAdornment: (
            <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
              {showNewPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          ),
        }}
      />

      <TextField
        required
        label="Neues Passwort bestätigen"
        name="confirmPassword"
        type={showConfirmPassword ? 'text' : 'password'}
        value={confirmPassword}
        onChange={handlePasswordChange}
        fullWidth
        margin="normal"
        error={!isMatching && confirmPassword !== ''}
        helperText={!isMatching && confirmPassword !== '' ? 'Passwörter stimmen nicht überein' : ''}
        InputProps={{
          endAdornment: (
            <>
              {isMatching === null ? null : isMatching ? <CheckCircleIcon sx={{ color: 'green' }} /> : <CancelIcon sx={{ color: 'red' }} />}
              <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </>
          ),
        }}
      />
      <Button variant="contained" color="primary" onClick={handleReset} disabled={!isMatching || passwordError} sx={{ mt: 2 }}>
        Passwort speichern
      </Button>
    </Box>
  );
}

export default ResetPassword;
